import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'

export interface ComplianceStatus {
  dailyChecklist: {
    bhx: { completed: boolean; date: string | null }
    lhr: { completed: boolean; date: string | null }
  }
  weeklyAudit: {
    daysSince: number | null
    lastDate: string | null
  }
  monthlyAudit: {
    samplesCount: number
    daysRemaining: number
  }
}

export function useETSFComplianceStatus() {
  const [status, setStatus] = useState<ComplianceStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true)
      setError(null)
      try {
        // Z64: Mid-week Checklist compliance is now "once per Mon-Fri week", not "once per
        // day" -- compute this ISO week's Monday..Friday window and check for any submission
        // in it. A Sat/Sun submission's checklist_date falls outside every week's Mon-Fri
        // range, so weekend submissions naturally don't count towards any week.
        const now = new Date()
        const dayOfWeek = now.getDay() // 0=Sun..6=Sat
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday)
        const friday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 4)
        const mondayStr = monday.toISOString().split('T')[0]
        const fridayStr = friday.toISOString().split('T')[0]

        // Fetch Mid-week Checklist submissions for this Mon-Fri window
        const { data: dailyChecklists } = await supabase
          .from('etsf_daily_checklists')
          .select('location, checklist_date')
          .gte('checklist_date', mondayStr)
          .lte('checklist_date', fridayStr)

        const bhxRecord = dailyChecklists?.find(c => c.location === 'BHX')
        const lhrRecord = dailyChecklists?.find(c => c.location === 'LHR')

        // Fetch latest weekly audit
        const { data: weeklyAudits } = await supabase
          .from('etsf_records')
          .select('record_date')
          .eq('record_type', 'weekly_audit')
          .order('record_date', { ascending: false })
          .limit(1)

        let daysSinceWeekly: number | null = null
        let lastWeeklyDate: string | null = null
        if (weeklyAudits && weeklyAudits.length > 0) {
          lastWeeklyDate = weeklyAudits[0].record_date
          if (lastWeeklyDate) {
            daysSinceWeekly = Math.floor(
              (new Date().getTime() - new Date(lastWeeklyDate).getTime()) / (1000 * 60 * 60 * 24)
            )
          }
        }

        // Fetch monthly audit samples count for current month
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0)

        const { data: monthlySamples } = await supabase
          .from('monthly_audit_samples')
          .select('id', { count: 'exact', head: false })
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString())

        const samplesCount = monthlySamples?.length || 0
        const daysInMonth = monthEnd.getDate()
        const currentDay = now.getDate()
        const daysRemaining = daysInMonth - currentDay

        setStatus({
          dailyChecklist: {
            bhx: { completed: !!bhxRecord, date: bhxRecord?.checklist_date ?? null },
            lhr: { completed: !!lhrRecord, date: lhrRecord?.checklist_date ?? null },
          },
          weeklyAudit: {
            daysSince: daysSinceWeekly,
            lastDate: lastWeeklyDate,
          },
          monthlyAudit: {
            samplesCount,
            daysRemaining,
          },
        })
      } catch (e: any) {
        setError(e.message || 'Failed to fetch compliance status')
      } finally {
        setLoading(false)
      }
    }

    fetchStatus()
    // Refresh every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  return { status, loading, error }
}
